import { serviceOrigin } from './services';
import { instanceReady, instanceNode } from '../hooks/useInstanceMode';
// Centralised log-api client. Two responsibilities that the original
// inline pattern (NewSessionDialog `log()` helper) silently got wrong
// and cost Alex a TTS-debug round-trip (Cloud's IACP 0087bd88):
//
//   1. Body shape is `{ entries: [...] }` per LogBatchCreate schema.
//      Bare `[...]` returns HTTP 422.
//   2. log-api does NOT auto-create sessions. POST /logs returns 404
//      until the session is created via POST /sessions.
//
// Plus a tertiary: `.catch(() => {})` swallowed those 422/404s so the
// integration looked healthy while never delivering a line. The new
// client surfaces non-OK responses to the browser console with a clear
// `[log-api]` prefix so future drift is loud.

const logBase = () => { const o = serviceOrigin('log'); return o ? `${o}/api/v1` : null; };
const SOURCE = 'cloud-v2';

// EIN zentrales Protokoll für alle Instanzen, auch für Kunden (Alex, 18.08.:
// „ich möchte nicht ein Log-API für jeden Tenant eigens ausrollen … dann
// könntest du bei einem Problem eine tenant-spezifische Abfrage machen").
//
// Das ist keine Umstellung, sondern schon der Zustand: Kundeninstanzen melden
// in `/api/version.services` KEINEN eigenen `log`-Ursprung (gemessen an Davids
// Instanz, 18.08.), und ein fehlender Schlüssel heißt Bestand → die Registry
// liefert das zentrale log-api. Was fehlte, war die Zuordnung: Ohne Tenant am
// Eintrag landen Davids Fehler und meine unter DENSELBEN Sitzungs-Namen im
// selben Topf, und genau die Abfrage, für die das Ganze zentral ist, geht nicht.
//
// Zwei Felder tragen sie, weil log-api genau nach diesen beiden filtern kann
// (`GET /sessions?app_id=…`, danach `GET /sessions/{id}/logs`):
//   • `app_id`      → `cloud-v2@david` — die Abfrage „zeig mir Davids Portal"
//   • Sitzungs-Name → `david-CloudV2-tts` — trennt die Sitzungen der Instanzen,
//                     sonst gewinnt der Erstanleger und alle schreiben hinein.
// `source` bleibt `cloud-v2`: Es benennt die ANWENDUNG, und bestehende
// Abfragen filtern danach.
//
// Ohne bekannte Instanz (Netzfehler beim Start) bleibt beides ungetaggt — ein
// falscher Tenant am Eintrag wäre schlimmer als ein fehlender, weil er die
// Fehlersuche in die falsche Instanz schickt.
function instanceTag(): string | null {
  const n = instanceNode();
  return n && n.trim() ? n.trim() : null;
}

export function scopedSession(sessionId: string): string {
  const tag = instanceTag();
  return tag ? `${tag}-${sessionId}` : sessionId;
}

export function scopedAppId(): string {
  const tag = instanceTag();
  return tag ? `${SOURCE}@${tag}` : SOURCE;
}

// Module-scope cache of sessions we've already ensured this page-load.
// Survives the lifecycle of one tab — refresh re-ensures (idempotent on
// the server, 409 / already-exists is fine).
const ensured = new Set<string>();

// Circuit-breaker against the "log the network failure OVER the failed
// network" cascade. When the connection flaps (net::ERR_NETWORK_CHANGED at
// a Mama's-WiFi / tunnel-drop), every SSE reconnect + poll fails AND each
// failure calls diag() → logToApi() → a POST that ALSO fails → console.warn
// → DebugConsole capture + re-render. On a flaky link that storm is what
// makes typing lag, not the underlying drop. Two guards:
//   1. navigator.onLine === false → don't even attempt (fully offline).
//   2. After any network throw, suppress all log-api POSTs for a cooldown
//      so one flap can't produce a POST-per-event flood.
const SUPPRESS_COOLDOWN_MS = 15_000;
let suppressUntil = 0;

function logApiMuted(): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  return Date.now() < suppressUntil;
}

async function ensureSession(sessionId: string): Promise<void> {
  if (ensured.has(sessionId)) return;
  if (logApiMuted()) return;
  try {
    const base = logBase();
    if (!base) return;
    const res = await fetch(`${base}/sessions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: sessionId,
        app_id: scopedAppId(),
        os: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
        app_version: (import.meta as { env?: Record<string, string> }).env?.VITE_APP_VERSION || 'dev',
      }),
    });
    // 200 created OR 409 already-exists both count as "ready to log".
    if (res.ok || res.status === 409) {
      ensured.add(sessionId);
    } else {
      console.warn(`[log-api] ensureSession(${sessionId}) failed: HTTP ${res.status}`);
    }
  } catch (e) {
    suppressUntil = Date.now() + SUPPRESS_COOLDOWN_MS;
    console.warn(`[log-api] ensureSession(${sessionId}) threw:`, e);
  }
}

export type LogType = 'Log' | 'Warning' | 'Error' | 'Exception';

/**
 * Append one log entry to a log-api session. Fire-and-forget — never
 * blocks the caller, never throws. The 4xx/5xx warning surfaces on the
 * browser console so silent wire-format drift can't recur.
 *
 * @param sessionId  log-api session id (e.g. "CloudV2-tts", "CloudV2-avatar")
 * @param type       one of Log | Warning | Error | Exception
 * @param message    the log line
 * @param extra      structured context — appended as `— {JSON}` suffix
 *                   because the schema has no metadata slot (Cloud IACP 3a5067dd)
 * @param stackTrace optional dedicated stack — rendered as a code block
 *                   in the dashboard. Use for Exception type.
 */
export async function logToApi(
  sessionId: string,
  type: LogType,
  message: string,
  extra?: Record<string, unknown>,
  stackTrace?: string,
): Promise<void> {
  if (logApiMuted()) return;
  // Erst die Instanz kennen, dann schreiben — sonst trüge die erste Zeile nach
  // dem Start keinen Tenant. Löst bei Fehler/404 sofort auf, niemand hängt.
  try { await instanceReady(); } catch { /* Bestand: ungetaggt schreiben */ }
  const scoped = scopedSession(sessionId);
  await ensureSession(scoped);
  if (logApiMuted()) return; // ensureSession may have just tripped the breaker
  const body = extra ? `${message} — ${JSON.stringify(extra)}` : message;
  const entry: Record<string, unknown> = { log_type: type, source: SOURCE, message: body };
  if (stackTrace) entry.stack_trace = stackTrace;
  try {
    const base = logBase();
    if (!base) return;
    const res = await fetch(`${base}/sessions/${scoped}/logs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ entries: [entry] }),
    });
    if (!res.ok) {
      let bodyHint = '';
      try { bodyHint = (await res.text()).slice(0, 200); } catch { /* network read failed */ }
      console.warn(`[log-api] POST ${scoped} failed: HTTP ${res.status} ${bodyHint}`);
    }
  } catch (e) {
    suppressUntil = Date.now() + SUPPRESS_COOLDOWN_MS;
    console.warn(`[log-api] POST ${scoped} threw:`, e);
  }
}
